/**
 * The account colours: the eight that come with the app, and the rules a ninth
 * of somebody's own has to satisfy.
 *
 * # Why the server sends a word
 *
 * This app renders in two themes -- `public/theme.js` picks one from
 * `prefers-color-scheme` and the toggle in the sidebar overrides it -- and a
 * colour chosen to read against white is routinely invisible against near-black.
 * A single stored `#4f46e5` therefore has to work on a background it was never
 * checked against, and the failure is silent to whoever chose it, because in
 * their theme it looked right.
 *
 * So the eight are words, and the *stylesheet* holds a pair of values for each
 * -- `--acct-blue` is redefined in the dark block, exactly like `--accent` and
 * everything else themeable. Contrast becomes a property of the design rather
 * than of the user's luck, and re-tuning a colour that turned out too dark is a
 * CSS change instead of a data migration.
 *
 * `hm_store::colour` on the daemon side has the palette's derivation: the eight
 * are spread across the blue-yellow and lightness axes that survive
 * deuteranopia and protanopia, rather than evenly around the hue wheel, which
 * is what produces a red and a green that are one colour to about one man in
 * twelve.
 *
 * # And why it will also send a hex
 *
 * Because "eight colours, take it or leave it" is a defensible answer to a
 * person who wants their work account in their employer's blue, and not a good
 * one. What made it defensible was the paragraph above, so a custom colour is
 * allowed exactly as far as that paragraph can be kept true:
 *
 * 1. **A custom colour is still a pair.** The stored hex is the light-theme
 *    half; `harmonise` derives the dark-theme half from it. Nobody picks a
 *    colour for a background they are not looking at, which is the failure the
 *    tokens exist to prevent, and deriving the second value means nobody has to.
 * 2. **Lightness is not the user's to choose.** Hue and chroma come through
 *    untouched -- they are what somebody means when they say "our blue" -- and
 *    lightness is pinned into the band the eight already occupy, once for each
 *    theme. A colour picked from the pale end of a colour wheel comes back
 *    readable rather than coming back invisible.
 * 3. **Distinguishability is advised, not enforced.** The eight are guaranteed
 *    to stay apart under dichromacy and a wheel cannot be; the picker measures
 *    the distance to the other accounts' colours and says when it is small.
 *    It does not refuse. This is somebody's own list of mailboxes, they can see
 *    it, and a mail client that argued with them about it would be answering a
 *    question they did not ask.
 *
 * The eight remain the path of least resistance -- they are the row of swatches,
 * and a custom colour is a button past the end of it -- which is the last and
 * least technical part of keeping the guarantee: almost nobody will need this,
 * and the people who do will be deliberate about it.
 *
 * # Colour is never the only signal
 *
 * Nothing in this app says which account something belongs to *by colour*.
 * Every coloured element sits beside the account's name, or carries it in an
 * accessible name -- the stripe on a message row is next to the row's account
 * tag, the sidebar dot is next to the group heading, the composer's dot is next
 * to a `<select>` whose value is the account. A reader who cannot see the
 * difference between rose and rust, or who is hearing the page rather than
 * looking at it, loses nothing but the shortcut. That is what makes point 3 a
 * warning rather than a rule.
 */

import type { Oklch } from "./colour-space";
import { contrast, fromOklch, oklchOf, parseHex, separation, toHex } from "./colour-space";
import { isDark } from "./settings";

/** Every named colour the daemon will accept, in the order the picker shows them. */
export const COLOURS = [
  "blue",
  "amber",
  "teal",
  "rose",
  "sky",
  "moss",
  "violet",
  "rust",
] as const;

export type Colour = (typeof COLOURS)[number];

/** A colour of somebody's own: `#rrggbb`, lowercase, and nothing else. */
export type CustomColour = `#${string}`;

/** What the `colour` field of an account may be, on the wire and in the store. */
export type AccountColour = Colour | CustomColour;

/**
 * What a swatch is called out loud.
 *
 * The reason the eight are words rather than hex strings: this is the
 * accessible name of the button in the picker, and the thing one person says to
 * another when they are looking at different screens. A custom colour has no
 * such name, which is a real cost of choosing one and is why the picker's own
 * button says "Custom colour" and reads the hex out after it.
 */
export const COLOUR_NAMES: Record<Colour, string> = {
  blue: "Blue",
  amber: "Amber",
  teal: "Teal",
  rose: "Rose",
  sky: "Sky",
  moss: "Moss",
  violet: "Violet",
  rust: "Rust",
};

/**
 * The lightness band a custom colour is pinned into, per theme, in OKLab L.
 *
 * Measured off the eight rather than invented. In the light theme they sit in
 * two tiers, four at L ~ 0.46-0.50 and four at L ~ 0.645-0.671; in the dark
 * theme the same eight sit at 0.637-0.660 and 0.740-0.855. The bands below are
 * those ranges, and the mapping between them is linear -- which is not a
 * coincidence but a check: run the light value of `blue` through it and the
 * answer is L 0.673, against the 0.659 the hand-tuned dark blue actually has,
 * with the other seven landing similarly close. A rule derived in the abstract
 * that lands on top of eight values arrived at by eye is a rule worth trusting.
 *
 * Both ends matter. Too dark in the light theme and a 4px stripe down a message
 * row reads as black rather than as a colour -- the same floor that set the
 * palette's own lower bound, and the narrowest thing any of these is ever drawn
 * as. Too light in the dark theme and it disappears into a #0e1014 background.
 */
const LIGHT_BAND = [0.45, 0.7] as const;
const DARK_BAND = [0.63, 0.86] as const;

/**
 * And then the floor the band turns out not to guarantee.
 *
 * The stylesheet holds the eight to 3:1 against the surfaces they are drawn on,
 * and pinning OKLab L does not deliver that on its own -- perceptual lightness
 * and relative luminance are different measures, and they disagree most for
 * exactly the colours somebody is most likely to pick out of a colour wheel. A
 * pure yellow at the top of the light band comes out at 2.3:1 on a white card,
 * which is a colour you cannot see, arrived at by a rule that was supposed to
 * be about being able to see it.
 *
 * So lightness is given up until the ratio is met: darker in the light theme,
 * lighter in the dark one. It moves nothing that was already legible -- none of
 * the eight, and no reasonable pick -- and it is what makes "this colour will
 * read on both backgrounds" a promise rather than an expectation.
 *
 * The two surfaces are the worst case in each theme rather than the commonest,
 * and the worst case is the surface *nearest in lightness to the colour*: the
 * darkest of the light theme's surfaces (`--surface-2`, which is what a hovered
 * message row is, stripe and all) and the lightest of the dark theme's
 * (`--raised`). The white card and the near-black page are the easy
 * backgrounds, not the hard ones. Written out here rather than read from the
 * stylesheet because this computes colours for both themes at once, and only
 * one theme's values are resolvable at a time.
 *
 * This is very slightly stricter than the eight hold themselves to -- four of
 * them measure between 2.8 and 3.0 against `--surface-2`, having been tuned
 * against the card and the page. They are not subject to this rule and do not
 * need to be: they were looked at, on both themes, by somebody who could see
 * them. A colour that arrives over HTTP gets no such inspection, so it gets the
 * arithmetic instead, and the arithmetic is not the place to shave a tenth.
 */
const MIN_CONTRAST = 3;
const LIGHT_SURFACE = "#eef0f4";
const DARK_SURFACE = "#1d2027";

/**
 * Two colours are worth mentioning to each other below this ΔE.
 *
 * The eight are 26 apart at their closest in the light theme and 23 in the dark
 * one, measured to whichever of normal, protanope and deuteranope vision
 * separates them least. Fifteen is comfortably tighter than either, so nothing
 * from the built-in palette can ever trip this against another built-in, and
 * only a deliberately similar custom colour will.
 */
const TOO_CLOSE = 15;

/**
 * The CSS colour for an account, in whichever theme the page is in.
 *
 * A `var()` reference for the eight, so the theme toggle recolours them without
 * anything re-rendering: the toggle changes an attribute on `<html>`, the custom
 * property resolves differently, and an inline style naming a resolved colour
 * would be stuck on whichever theme was current when the element was drawn.
 *
 * `light-dark()` for the ninth, which is the same trick by another route. There
 * is no stylesheet declaration to redefine for a colour that arrived over HTTP
 * five seconds ago, and the alternatives -- injecting a `<style>` element per
 * account, or re-rendering every dot on the theme toggle -- are both machinery
 * where a CSS function will do. It resolves against the element's used
 * `color-scheme`, which the root keeps in step with the theme in the stylesheet
 * and again in `settings.watchTheme`.
 *
 * The signature is unchanged and deliberately still takes only the colour: the
 * message list and the sidebar call this with a value out of the colour map and
 * must not have to learn that there are two kinds now.
 */
export function colourVar(colour: string | null | undefined): string {
  if (isColour(colour)) return `var(--acct-${colour})`;
  if (isCustom(colour)) {
    const pair = harmonise(colour);
    return `light-dark(${pair.light}, ${pair.dark})`;
  }
  // A colour a newer server knows and this client does not, or nothing at all.
  return "var(--acct-blue)";
}

export function isColour(value: unknown): value is Colour {
  return typeof value === "string" && (COLOURS as readonly string[]).includes(value);
}

/** Whether this is a stored custom colour, in the one form the daemon stores. */
export function isCustom(value: unknown): value is CustomColour {
  return typeof value === "string" && /^#[0-9a-f]{6}$/.test(value);
}

/**
 * Read what somebody typed, and give back the one form the daemon will take.
 *
 * Liberal here and strict on the wire, which is the only place that split
 * belongs. A person pasting a colour out of a brand guide types `4F46E5` or
 * `#4F46E5` or `#4f4` with a space on the end, and refusing those would be
 * pedantry about a syntax nobody memorises. The daemon accepts exactly one
 * spelling and validates it again on arrival, because it is the side that has
 * to assume the client is lying.
 *
 * Returns the colour as typed, canonicalised but not yet legible: `harmonise`
 * is a separate step so that the picker can show the difference between what
 * was chosen and what will be stored, rather than silently moving the swatch
 * under somebody's cursor.
 */
export function readCustom(typed: string): CustomColour | null {
  const raw = typed.trim().replace(/^#/, "").toLowerCase();
  const hex =
    /^[0-9a-f]{3}$/.test(raw)
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  return /^[0-9a-f]{6}$/.test(hex) ? (`#${hex}` as CustomColour) : null;
}

/** A colour's two halves: what it looks like in each theme. */
export interface Pair {
  light: CustomColour;
  dark: CustomColour;
}

/**
 * Pin a colour's lightness into the two bands, keeping its hue.
 *
 * The light half is the colour itself unless it is outside the band, in which
 * case it is moved to the nearest edge -- so a colour that was already going to
 * read properly comes back untouched, and only the pale yellows and the near
 * blacks are moved at all. Then `legible` darkens it further if the band alone
 * did not buy 3:1 against a white card, which for some hues it does not. The
 * dark half is that final lightness mapped linearly onto the dark band, and
 * lightened by the same rule against the dark theme's palest surface.
 *
 * Chroma is never raised and is lowered only where the gamut demands it: a hue
 * at L 0.63 may simply not exist in sRGB at the chroma it had at L 0.30, and
 * `fromOklch` gives up chroma rather than lightness or hue when it has to.
 *
 * Idempotent, which matters more than it looks: the value stored on the daemon
 * is the light half, so this runs again on it every time the colour is read
 * back, and a rule that drifted would walk somebody's colour a little further
 * from what they chose on every page load.
 */
export function harmonise(colour: CustomColour): Pair {
  const cached = pairs.get(colour);
  if (cached) return cached;

  const rgb = parseHex(colour);
  // Unreachable through `isCustom`, but this is called with server data and a
  // black swatch is a better outcome than a thrown exception in a mail list.
  if (!rgb) return { light: colour, dark: colour };

  const { l, c, h } = oklchOf(rgb);
  const light = legible({ l: Math.min(LIGHT_BAND[1], Math.max(LIGHT_BAND[0], l)), c, h }, LIGHT_SURFACE, 0.1);
  // From the light half's final lightness rather than from the colour as
  // typed, because the light half is what gets stored -- and the dark one has
  // to be reproducible from it alone, on a later page load with the original
  // pick long gone.
  const across =
    DARK_BAND[0] +
    ((light.l - LIGHT_BAND[0]) / (LIGHT_BAND[1] - LIGHT_BAND[0])) * (DARK_BAND[1] - DARK_BAND[0]);
  const dark = legible({ l: across, c, h }, DARK_SURFACE, 0.99);

  const pair: Pair = { light: light.hex, dark: dark.hex };
  pairs.set(colour, pair);
  return pair;
}

/**
 * Move a colour's lightness towards `limit` until it stands out from `against`.
 *
 * A bisection rather than a step-and-check loop, because contrast against a
 * fixed background is monotonic in lightness on either side of it: once the far
 * end is known to pass and the near end to fail, the boundary is sixteen
 * halvings away and every intermediate answer is a real colour.
 *
 * Returns the lightness as well as the colour. The caller needs it: the dark
 * half of a pair is derived from where the light half *ended up*, not from
 * where it was aimed.
 */
function legible(target: Oklch, against: string, limit: number): { hex: CustomColour; l: number } {
  const surface = parseHex(against);
  const at = (l: number): [CustomColour, number] => {
    const hex = toHex(fromOklch({ ...target, l })) as CustomColour;
    const rgb = parseHex(hex);
    return [hex, surface && rgb ? contrast(rgb, surface) : MIN_CONTRAST];
  };

  const [hex, ratio] = at(target.l);
  if (ratio >= MIN_CONTRAST) return { hex, l: target.l };

  let fails = target.l;
  let passes = limit;
  for (let i = 0; i < 16; i += 1) {
    const mid = (fails + passes) / 2;
    if (at(mid)[1] >= MIN_CONTRAST) passes = mid;
    else fails = mid;
  }
  return { hex: at(passes)[0], l: passes };
}

/**
 * A small cache, because this runs per rendered dot.
 *
 * Bounded by the number of distinct colours an account list has ever held,
 * which is bounded by the number of accounts. Nothing evicts, and nothing needs
 * to: the entries are two strings each and the map cannot grow to a size worth
 * a policy.
 */
const pairs = new Map<string, Pair>();

/**
 * The colour an account is actually painted in right now, as a hex.
 *
 * Reads the live stylesheet for the eight, because the stylesheet is where
 * their two values live and there is no second copy of them in the client --
 * duplicating them here is how they would drift from the ones on screen. So
 * this answers for the theme the page is in, and the answer changes when the
 * toggle is pressed. That is the right frame for the only thing it is used for:
 * telling somebody that two colours in front of them look alike.
 */
export function resolvedColour(colour: string): string | null {
  if (isCustom(colour)) {
    const pair = harmonise(colour);
    return isDark.value ? pair.dark : pair.light;
  }
  if (!isColour(colour)) return null;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--acct-${colour}`)
    .trim();
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : null;
}

/**
 * The nearest of `others` to `colour`, if any of them is near enough to mention.
 *
 * Returns the index into `others`, so the caller can name the account rather
 * than the colour -- "hard to tell apart from Work" is actionable and "ΔE 11" is
 * not. Anything that cannot be resolved to a colour is skipped rather than
 * treated as black, which would produce a warning about an account whose colour
 * this client does not understand.
 */
export function nearest(colour: string, others: string[]): number | null {
  const mine = parseHexOf(colour);
  if (!mine) return null;
  let closest: number | null = null;
  let best = TOO_CLOSE;
  for (let index = 0; index < others.length; index += 1) {
    const theirs = parseHexOf(others[index] ?? "");
    if (!theirs) continue;
    const distance = separation(mine, theirs);
    if (distance < best) {
      best = distance;
      closest = index;
    }
  }
  return closest;
}

function parseHexOf(colour: string) {
  const hex = resolvedColour(colour);
  return hex ? parseHex(hex) : null;
}
